import { useOutletContext, Link, useNavigate } from 'react-router-dom';
import {
  Package, Heart, MapPin, Bell, HelpCircle, FileText, Shield, LogOut,
  ChevronRight, Settings, Gift, Wallet, Headphones, Languages, Star,
} from 'lucide-react';

const ITEMS = [
  { group: 'Your Activity', items: [
    { id: 'orders', icon: Package, label: 'My Orders', sub: 'Track, return, or buy again', href: '/shop/orders' },
    { id: 'wishlist', icon: Heart, label: 'Wishlist', sub: 'Saved items', href: '/shop/wishlist' },
    { id: 'wallet', icon: Wallet, label: 'DharmShop Wallet', sub: 'Refunds, rewards, gift money', href: '/shop/wallet' },
    { id: 'rewards', icon: Gift, label: 'Rewards & Coupons', sub: 'Offers tied to your account', href: '/shop/rewards' },
  ]},
  { group: 'Account', items: [
    { id: 'addresses', icon: MapPin, label: 'Saved Addresses', sub: 'Home, work & more', href: '/shop/addresses' },
    { id: 'notifications', icon: Bell, label: 'Notifications', sub: 'Order updates & offers', href: '/notifications' },
    { id: 'language', icon: Languages, label: 'Language', sub: 'English / हिन्दी', href: '/settings' },
    { id: 'settings', icon: Settings, label: 'Account Settings', sub: 'Profile, password, privacy', href: '/settings' },
  ]},
  { group: 'Support', items: [
    { id: 'help', icon: HelpCircle, label: 'Help Centre', sub: '24/7 support', href: '/shop/help' },
    { id: 'contact', icon: Headphones, label: 'Contact Us', sub: 'WhatsApp, email, call', href: '/shop/contact' },
    { id: 'rate', icon: Star, label: 'Rate DharmBook', sub: 'Share your love on PlayStore', href: '#' },
    { id: 'terms', icon: FileText, label: 'Terms & Privacy', sub: 'Legal information', href: '/legal' },
    { id: 'about', icon: Shield, label: 'About DharmShop', sub: 'Sacred marketplace of Bharat', href: '/about' },
  ]},
];

export default function ShopMenuPage() {
  const { user } = useOutletContext();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('dharmbook_token');
    localStorage.removeItem('dharmbook_user');
    navigate('/auth');
  };

  return (
    <div className="shop-page" data-testid="shop-menu-page" style={{ paddingBottom: 100 }}>
      {/* Account header */}
      <div className="shop-fk-section" style={{ marginTop: 8 }}>
        {user ? (
          <Link to="/profile" className="flex items-center gap-3" data-testid="menu-account-header">
            {user.avatar && !user.avatar.includes('ui-avatars.com') ? (
              <img src={user.avatar} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-orange-100" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-lg font-bold text-orange-700">
                {user.name?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold text-gray-900 truncate">{user.name}</div>
              <div className="text-xs text-gray-500 truncate">{user.email}</div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </Link>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-bold">Welcome, Devotee</div>
              <div className="text-xs text-gray-500">Login for orders, wallet & more</div>
            </div>
            <Link to="/auth" className="px-4 py-2 bg-[#FF7A00] text-white font-bold rounded-lg text-sm" data-testid="menu-login-btn">
              Login
            </Link>
          </div>
        )}
      </div>

      {/* Sections */}
      {ITEMS.map((group) => (
        <div className="shop-fk-section" key={group.group}>
          <h3 className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">{group.group}</h3>
          <div className="divide-y divide-gray-100">
            {group.items.map((item) => (
              <Link
                key={item.id}
                to={item.href}
                className="flex items-center gap-3 py-3"
                data-testid={`menu-item-${item.id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-800">{item.label}</div>
                  <div className="text-xs text-gray-500 truncate">{item.sub}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      ))}

      {/* Logout */}
      {user && (
        <div className="shop-fk-section">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full text-red-600 font-semibold py-3"
            data-testid="menu-logout-btn"
          >
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
              <LogOut className="w-5 h-5" />
            </div>
            <span className="text-sm">Log out</span>
          </button>
        </div>
      )}

      <div className="text-center text-xs text-gray-400 py-4">DharmBook · Sacred marketplace of Bharat</div>
    </div>
  );
}
